<script lang="ts" setup>
    import {useModal} from "../composables/useModal";

    import DContainer from "../atoms/DContainer.vue";
    import DImg from "../atoms/DImg.vue";
    import DButton from "../atoms/DButton.vue";
    import DText from "../atoms/DText.vue";

    import DVideoModal from "../overlays/DVideoModal.vue";

    import type {BaseCardType, RevealType} from "@songfinch/types";

    const props = defineProps<
        BaseCardType &
        RevealType & {
            dataTestId: string;
        }
    >();

    const {modalActive, openModal, closeModal} = useModal({
        trackingProperties: {
            type: "video_opened",
            payload: {
                video_title: props?.video?.title || "",
            },
        },
    });
</script>

<template>
    <DContainer
        v-if="video && placeholder"
        class="d-reveal-card"
        :data-test-td="dataTestId"
        theme="dark"
        @click="openModal"
    >
        <DImg
            :key="placeholder.title"
            class="d-reveal-image"
            :width="placeholder.width"
            :height="placeholder.height"
            :alt="placeholder.title"
            :src="placeholder.url"
        />
        <DContainer class="d-reveal-card-utils">
            <DButton
                class="d-reveal-card-button"
                :data-test-id="`${dataTestId}-play-video`"
                theme="dark"
                variant="circle"
                icon="play"
                :aria-label="`Play ${video.title}`"
            />
            <DText class="d-reveal-card-text" variant="p-3-thicc" type="p">
                {{title}}
            </DText>
            <DText
                class="d-reveal-card-subtext"
                color="muted"
                variant="p-3"
                type="p"
            >
                {{subtext}}
            </DText>
        </DContainer>
    </DContainer>

    <Teleport v-if="video" to="body">
        <DVideoModal
            v-if="modalActive"
            :data-test-id="`${dataTestId}-video-modal`"
            v-bind="video"
            :src="video.url"
            @close="closeModal"
        />
    </Teleport>
</template>

<style lang="scss" scoped>
.d-reveal-card {
  position: relative;
  display: grid;
  cursor: pointer;
  border-radius: var(--radius-16);
  overflow: hidden;
  width: 270px;
  height: 352px;

  &:after {
        position: absolute;
        width: 100%;
        height: 50%;
        content: "";
        background: linear-gradient(180deg, rgba(28, 27, 28, 0) -0%, rgba(28, 27, 28, .5) 100%);
        mix-blend-mode: multiply;
        z-index: 2;
        top: 50%;
    }
}

.d-reveal-image {
  object-fit: cover;
  height: 100%;
  width: 100%;
}

.d-reveal-card-utils {
  display: grid;
  position: absolute;
  grid-template-columns: 52px auto;
  column-gap: 10px;
  bottom: 28px;
  left: 20px;
  z-index: 3;
}

.d-reveal-card-button {
  width: 52px;
  height: 52px;
  grid-row: span 2;
}

.d-reveal-card-text,
.d-reveal-card-subtext {
  text-align: left;
  grid-column: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
}
</style>
